// Typed client for /api/v1/rides/* (ONDC TRV10 on-demand ride hailing) over the
// shared authenticated fetch core (lib/http.ts). Near-twin of ticketsApi, minus
// payment (the driver collects the fare on fulfillment) plus track/rate.
import { request, uploadBinary, q } from './http';
import type {
  Ride,
  RideDraft,
  RideTracking,
  RideContact,
  RideSearchRequest,
  RideSelectRequest,
  RideInitRequest,
  RideConfirmRequest,
  RideCancelRequest,
  RideRateRequest,
  RideRaiseIssueRequest,
  RideUpdateStopsRequest,
  RideTipRequest,
  RideTransactionAck,
  ListRidesResponse,
  RideIssue,
  RideListIssuesResponse,
  RideIssueAck,
} from './rides-types';

export const ridesApi = {
  // Booking flow (async draft, polled via useRideDraft → useBeckonDraft).
  search: (body: RideSearchRequest) =>
    request<RideTransactionAck>('POST', '/api/v1/rides/search', body),
  draft: (txnId: string) => request<RideDraft>('GET', `/api/v1/rides/draft/${q(txnId)}`),
  select: (body: RideSelectRequest) =>
    request<RideTransactionAck>('POST', '/api/v1/rides/select', body),
  init: (body: RideInitRequest) => request<RideTransactionAck>('POST', '/api/v1/rides/init', body),
  // No payment fields — TRV10 is BPP-collected ON-FULFILLMENT.
  confirm: (body: RideConfirmRequest) =>
    request<RideTransactionAck>('POST', '/api/v1/rides/confirm', body),

  // Orders.
  list: (limit = 20, offset = 0) =>
    request<ListRidesResponse>('GET', `/api/v1/rides?limit=${limit}&offset=${offset}`),
  ride: (id: string) => request<Ride>('GET', `/api/v1/rides/${q(id)}`),
  // Forces a fresh on_status round-trip from the BPP, then the order snapshot updates.
  status: (id: string) => request<RideTransactionAck>('GET', `/api/v1/rides/${q(id)}/status`),
  // 404 (RIDE_005) until the first driver ping — treat as "locating driver" and keep polling.
  track: (id: string) => request<RideTracking>('GET', `/api/v1/rides/${q(id)}/track`),
  cancel: (id: string, body: RideCancelRequest) =>
    request<RideTransactionAck>('POST', `/api/v1/rides/${q(id)}/cancel`, body),
  rate: (id: string, body: RideRateRequest) =>
    request<RideTransactionAck>('POST', `/api/v1/rides/${q(id)}/rate`, body),
  support: (id: string) => request<RideContact>('GET', `/api/v1/rides/${q(id)}/support`),

  // 2.1.0 post-confirm mutations (do NOT advance the booking draft — they mutate the
  // live order). `stops` adds an intermediate stop (mode ADD) or changes the END stop
  // via a 2-stage SOFT→CONFIRM handshake (mode CHANGE); `tip` appends a post-order tip.
  stops: (id: string, body: RideUpdateStopsRequest) =>
    request<RideTransactionAck>('POST', `/api/v1/rides/${q(id)}/stops`, body),
  tip: (id: string, body: RideTipRequest) =>
    request<RideTransactionAck>('POST', `/api/v1/rides/${q(id)}/tip`, body),

  // IGM (issue & grievance management) — mirrors ticketsApi.
  raiseIssue: (id: string, body: RideRaiseIssueRequest) =>
    request<RideIssueAck>('POST', `/api/v1/rides/${q(id)}/issues`, body),
  // Reuse the shared, domain-agnostic attachment endpoint (bytes → public, BPP-fetchable URL).
  uploadIssueAttachment: (file: File) =>
    uploadBinary<{ url: string }>('/api/v1/tickets/issues/attachments', file).then((r) => r.url),
  listAllIssues: (limit = 50, offset = 0) =>
    request<RideListIssuesResponse>('GET', `/api/v1/rides/issues?limit=${limit}&offset=${offset}`),
  listIssues: (id: string) =>
    request<RideListIssuesResponse>('GET', `/api/v1/rides/${q(id)}/issues`),
  issue: (issueId: string) => request<RideIssue>('GET', `/api/v1/rides/issues/${q(issueId)}`),
  updateIssue: (issueId: string, body: { actionCode?: string; shortDesc?: string }) =>
    request<RideIssueAck>('POST', `/api/v1/rides/issues/${q(issueId)}/update`, body),
  escalateIssue: (issueId: string, body: { shortDesc?: string }) =>
    request<RideIssueAck>('POST', `/api/v1/rides/issues/${q(issueId)}/escalate`, body),
  closeIssue: (issueId: string, body: { rating?: string }) =>
    request<RideIssueAck>('POST', `/api/v1/rides/issues/${q(issueId)}/close`, body),
};
